import { CustomElement } from "../../../core/CustomElement";
import SelectItem from "../../../core/interfaces/SelectItem";

import ko from "knockout";

export default class SelectManyVM {
  $customElement: CustomElement;
  fieldWidth: KnockoutObservable<string>;
  fieldHeight: KnockoutObservable<string>;
  fieldLeft: KnockoutObservable<string>;
  minLength: KnockoutObservable<number>;
  minSelectedItems: KnockoutObservable<number>;
  maxSelectedItems: KnockoutObservable<number>;
  readOnly: KnockoutComputed<boolean>;
  options: KnockoutObservableArray<SelectItem>;
  disabled: KnockoutObservable<boolean>;
  filteredOptions: KnockoutObservableArray<SelectItem>;
  rawValue: KnockoutObservable<string>;
  rawValueChange: KnockoutComputed<string>;
  rawValueChangeSubscriptionId: KnockoutSubscription;
  focus: KnockoutObservable<boolean>;
  focusChange: KnockoutComputed<boolean>;
  focusChangeSubscriptionId: KnockoutSubscription;
  displayList: KnockoutObservable<boolean>;
  value: KnockoutObservableArray<SelectItem>;
  displayLoader: KnockoutObservable<boolean>;
  onAddItem: (item: SelectItem) => void | null;
  onRemoveItem: (item: SelectItem) => void | null;

  constructor($customElement: CustomElement) {
    this.$customElement = $customElement;
    this.fieldWidth = ko.observable();
    this.fieldHeight = ko.observable();
    this.fieldLeft = ko.observable();
    this.minLength = ko.observable(0);
    this.minSelectedItems = ko.observable();
    this.disabled = ko.observable();
    ko.pureComputed(this.minSelectedItems).subscribe((val: number) => {
      if (val) {
        this.validateSelectedItems();
      }
    }, this);
    this.maxSelectedItems = ko.observable();
    ko.pureComputed(this.maxSelectedItems).subscribe((val: number) => {
      if (val) {
        this.validateSelectedItems();
      }
    }, this);
    this.readOnly = ko.pureComputed(
      () =>
        this.maxSelectedItems() &&
        this.value().length >= this.maxSelectedItems(),
      this
    );
    this.options = ko.observableArray();
    this.filteredOptions = ko.observableArray();
    this.rawValue = ko.observable("");
    this.rawValueChange = ko.pureComputed(this.rawValue);
    this.rawValueChangeSubscriptionId = this.rawValueChange.subscribe(
      (val: string) => {
        if (val.length >= 0) {
          this.showOptionsList();
        } else {
          this.hideOptionsList();
        }
      },
      this
    );
    this.focus = ko.observable(false);
    this.focusChange = ko.pureComputed(this.focus);
    this.focusChangeSubscriptionId = this.focusChange.subscribe(
      (val: boolean) => {
        if (val) {
          this.showOptionsList();
        } else {
          this.hideOptionsList();
        }
      },
      this
    );
    this.displayList = ko.observable(false);
    this.value = ko.observableArray([]);
    this.displayLoader = ko.observable(false);
    this.onAddItem = null;
    this.onRemoveItem = null;
  }

  onValueChange(
    callback: ((viewModel: this, val: SelectItem[]) => void) | null
  ): void {
    if (callback) {
      this.value.subscribe((val: SelectItem[]) => {
        callback(this, val);
      }, this);
    }
  }

  onRawValueChange(
    callback: ((viewModel: this, val: string) => void) | null,
    timeout: number | null
  ): void {
    this.rawValueChangeSubscriptionId.dispose();
    if (timeout) {
      this.rawValueChange.extend({
        rateLimit: { method: "notifyWhenChangesStop", timeout: timeout },
      });
    }
    if (callback) {
      this.rawValueChange.subscribe((val: string) => {
        callback(this, val);
      }, this);
    }
  }

  onFocusChange(
    callback: ((viewModel: this, val: boolean) => void) | null
  ): void {
    this.focusChangeSubscriptionId.dispose();
    if (callback) {
      this.focusChange.subscribe((val: boolean) => {
        callback(this, val);
      }, this);
    }
  }

  click(data: this, event: MouseEvent): void {
    if (
      !this.maxSelectedItems() ||
      this.value().length < this.maxSelectedItems()
    ) {
      if (
        event.target instanceof HTMLElement &&
        ((<HTMLElement>event.target).classList.contains("field") ||
          (<HTMLElement>event.target).id === "icon")
      ) {
        this.$customElement.shadowDom.getElementById("input").focus();
      }
    }
  }

  getFocus(): void {
    if (
      !this.maxSelectedItems() ||
      this.value().length < this.maxSelectedItems()
    ) {
      this.focus(true);
    }
  }

  loseFocus(): void {
    this.rawValue("");
    this.hideOptionsList();
    this.focus(false);
  }

  showOptionsList(): void {
    const $component = <HTMLElement>(
      (<CustomElement>(
        this.$customElement.shadowDom.querySelector("ccc-field")
      )).shadowDom.querySelector(".component")
    );
    this.fieldWidth($component.getBoundingClientRect().width + "px");
    this.fieldHeight($component.offsetHeight - 2 + "px");
    // this.fieldLeft(($component.offsetLeft - 1) + "px");
    this.fieldLeft("-1px");
    this.filteredOptions(this.options());
    this.filterOptions();
    this.displayList(true);
  }

  hideOptionsList(): void {
    this.displayList(false);
  }

  filterOptions(): void {
    if (this.options() || this.options.length > 0) {
      this.filteredOptions(
        this.filteredOptions().filter(
          (option: SelectItem) =>
            option.name.toLowerCase().indexOf(this.rawValue().toLowerCase()) >
            -1
        )
      );
      this.value().forEach((item: SelectItem) => {
        this.filteredOptions(
          this.filteredOptions().filter(
            (option: SelectItem) =>
              option.name.toLowerCase() !== item.name.toLowerCase()
          )
        );
      });
    }
  }

  addItem(itemToAdd: SelectItem): void {
    // Commented: Allow add programmatically more tags without validation.
    // if (
    //   !this.maxSelectedItems() ||
    //   this.value().length < this.maxSelectedItems()
    // ) {
    this.hideOptionsList();
    this.value.push(itemToAdd);
    this.options(
      this.options().filter((item: SelectItem) => item.name !== itemToAdd.name)
    );
    this.rawValue("");
    this.$customElement.shadowDom.getElementById("input").blur();
    if (this.onAddItem) {
      this.onAddItem(itemToAdd);
    }
    if (this.minSelectedItems() || this.maxSelectedItems()) {
      this.validateSelectedItems();
    }
    // }
  }

  removeItem(itemToRemove: SelectItem): void {
    ko.tasks.schedule(() => {
      this.validateSelectedItems();
    });

    this.hideOptionsList();
    this.value(
      this.value().filter((item: SelectItem) => item.name !== itemToRemove.name)
    );
    this.options.push(itemToRemove);
    this.options.sort((a: SelectItem, b: SelectItem) => {
      if (a.name > b.name) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      return 0;
    });
    this.rawValue("");
    this.$customElement.shadowDom.getElementById("input").blur();
    if (this.onRemoveItem) {
      this.onRemoveItem(itemToRemove);
    }
  }

  validateSelectedItems(): void {
    const cccField = this.$customElement.shadowDom.querySelector("ccc-field");
    const inputField = this.$customElement.shadowDom.querySelector("#input");

    if (
      this.minSelectedItems() &&
      this.value().length < this.minSelectedItems()
    ) {
      cccField.setAttribute(
        "message",
        '{"text": "Please apply minimum ' +
          this.minSelectedItems() +
          (this.minSelectedItems() == 1 ? " tag" : " tags") +
          '", "severity": "error"}'
      );
    } else if (
      this.maxSelectedItems() &&
      this.value().length == this.maxSelectedItems()
    ) {
      //Add warning until user refocuses
      inputField.addEventListener(
        "focus",
        () => {
          cccField.setAttribute(
            "message",
            '{"text": "Maximum ' +
              this.maxSelectedItems() +
              (this.maxSelectedItems() == 1 ? " tag" : " tags") +
              ' can be applied. Remove a tag to add a different tag.", "severity": "warning"}'
          );
        },
        { once: true }
      );
    } else if (
      this.maxSelectedItems() &&
      this.value().length > this.maxSelectedItems()
    ) {
      const extraTags = this.value().length - this.maxSelectedItems();
      cccField.setAttribute(
        "message",
        '{"text": "More than maximum ' +
          this.maxSelectedItems() +
          (this.maxSelectedItems() == 1 ? " tag" : " tags") +
          " selected.  Please remove " +
          extraTags +
          (extraTags === 1 ? " tag" : " tags") +
          ' to continue.", "severity": "error"}'
      );
    } else {
      cccField.removeAttribute("message");
    }
  }
}
